/**
 * Admin API methods for one domain's contacts, delegation, documents, EPP, commerce,
 * transfers, audit and VNNIC sync state, plus registry contact objects, transfers and registrant changes
 * (subsystems/registrar/facade/adapters/admin-api/src/admin/core.rs).
 *
 * Subject names are only present for callers who may read subjects; otherwise they are null
 * and the UI shows the subject type instead.
 */

import type { CallOptions, DecimalString, ISODateTime, LifecycleState, RegistryCommandState, VnnicSyncOperation, VnnicSyncErrorCode, VnnicSyncStatus, VnnicTransactionStatus, RecordedReason, WriteOptions } from '../types';
import type { ReconciliationIssue } from './registry';

export type ContactRole = 'REGISTRANT' | 'ADMIN' | 'TECH' | 'BILLING';

/** `POST /domains/{id}/registration/reactivate` (registration_recovery.rs `Reactivated`). */
export interface RegistrationReactivated {
  workflow_id: string;
  order_item_id: string;
  /** The step the workflow runs next (`dossier`). */
  step: string;
  /** The amount held again from the customer's wallet, and what the wallet can spend after it. */
  held: DecimalString;
  currency: string;
  available: DecimalString;
  /** The new domain create's idempotency key (the refused create stays history). */
  create_key: string;
  nameservers: string[];
  dossier_corrected: string | null;
  verification_attached: string | null;
}

// ---------------------------------------------------------------------------
// Domain tabs

/** One contact assignment of the domain (`domain.domain_contacts`). */
export interface DomainContact {
  id: string;
  role: ContactRole;
  /** Registry contact object ID; null for registries without contact objects. */
  handle: string | null;
  valid_from: ISODateTime;
  /** Null while the assignment is current. */
  valid_until: ISODateTime | null;
  subject_id: string | null;
  subject_type: string | null;
  subject_version_number: number | null;
  subject_version_is_current: boolean | null;
  /** Legal name of the subject version (null without `subject.read`). */
  display_name: string | null;
  country_code: string | null;
  /** Domains currently using this handle at the same registry. */
  domain_count: number;
  subject_version_id?: string | null;
  subject_current_version_id?: string | null;
  /** How the contact got these details: cloned at registration, synced from the subject, or edited for this domain only. */
  origin?: ContactOrigin;
  /** When the domain's contact for this role was first cloned from the subject. */
  cloned_at?: ISODateTime | null;
  /** Current contacts: fields in which the subject's current details differ. */
  changed_fields?: ContactField[];
  /** Current contacts: the pinned subject version's data as the registry receives it; null without `subject.read` and on past assignments. */
  details?: ContactData | null;
}

export type ContactOrigin = 'CLONED' | 'SYNCED' | 'EDITED';
export type ContactField = 'legal_name' | 'identifier' | 'address' | 'email' | 'phone' | 'fax' | 'details';
/** A domain contact against its subject: CURRENT, BEHIND (older details, follows a sync), EDITED (set for the domain only), SYNCING. */
export type ContactSyncStatus = 'CURRENT' | 'BEHIND' | 'EDITED' | 'SYNCING';

/** `GET /subjects/{id}/domain-contacts`: a domain contact cloned from the subject. */
export interface SubjectDomainContact {
  domain_id: string;
  ascii_name: string;
  /** The domain's name as people read it. */
  domain: string;
  lifecycle_state: string;
  role: ContactRole;
  handle: string | null;
  subject_version_id: string | null;
  version_number: number | null;
  origin: ContactOrigin;
  since: ISODateTime;
  cloned_at: ISODateTime;
  status: ContactSyncStatus;
  changed_fields: ContactField[];
}

/** What "Đồng bộ sang tên miền" did for one domain. */
export type DomainSyncResult =
  | { outcome: 'STARTED'; domain_id: string; workflow_id: string; roles: ContactRole[] }
  | { outcome: 'REGISTRANT_CHANGE'; domain_id: string; request: 'REGISTRANT_CHANGE' | 'PENDING_CONFIRMATION' | 'NAME_CHANGE'; id: string }
  | { outcome: 'UP_TO_DATE'; domain_id: string }
  | { outcome: 'SKIPPED'; domain_id: string; reason: string };

/** `GET /domains/{key}/contacts` */
export interface DomainContacts {
  registry_code: string;
  registry_name: string;
  supports_contact_objects: boolean | null;
  /** A contact update of this domain is in progress. */
  sync_running?: boolean;
  items: DomainContact[];
}

/** `GET /domains/{key}/registry-state` (routes/registry.rs): the registry's last answer and the discrepancies found. */
export interface DomainRegistryState {
  /** Null until reconciliation has read the domain at the registry. */
  snapshot: {
    captured_at: ISODateTime;
    registry_transaction_id: string | null;
    roid: string | null;
    /** The sponsoring registrar's id at the registry. */
    sponsor: string | null;
    statuses: string[] | null;
    nameservers: string[] | null;
    /**
     * Addresses (glue) the registry's domain view carried, per name server reported with any
     * (host attributes, e.g. VNNIC); `[]`: none. null: not compared (host objects / REST
     * registry, or an older snapshot).
     */
    host_addrs?: { host: string; addresses: string[] }[] | null;
    created_at: ISODateTime | null;
    expires_at: ISODateTime | null;
  } | null;
  /** Open first, then the latest resolved (20 at most); null without `registry.transaction.read`. */
  issues: ReconciliationIssue[] | null;
}

/**
 * `POST /domains/{key}/registry-sync` (routes/registry_write.rs): the registry read now through the
 * reconciliation's reader. `fields`: our copies refreshed (`registry_expires_at`,
 * `registry_statuses`) or the issue types opened for review (`NAMESERVER`, `EXISTENCE`,
 * `SPONSORSHIP`). A read without an answer is an error (`REGISTRY_UNKNOWN`, `REGISTRY_REJECTED`,
 * `REGISTRY_UNAVAILABLE`), never `IN_SYNC`.
 */
export interface DomainRegistrySync {
  outcome: 'IN_SYNC' | 'REFRESHED' | 'ISSUES_OPENED';
  fields: string[];
  synced_at: ISODateTime;
}

export interface DomainNameserver {
  host: string;
  /** Glue addresses (in-bailiwick hosts only). */
  addresses: string[];
  in_bailiwick: boolean;
  updated_at: ISODateTime;
}

export interface DomainDsRecord {
  key_tag: number;
  algorithm: number;
  digest_type: number;
  digest: string;
  key_flags: number | null;
  key_protocol: number | null;
  key_algorithm: number | null;
  public_key: string | null;
  updated_at: ISODateTime;
}

/** `GET /domains/{key}/delegation` */
export interface DomainDelegation {
  dns_state: string;
  registry_code: string;
  registry_name: string;
  supports_dnssec: boolean | null;
  supports_host_objects: boolean | null;
  nameservers: DomainNameserver[];
  ds_records: DomainDsRecord[];
}

/** A document bound to one of the domain's dossier versions (metadata only). */
export interface DomainDocument {
  binding_id: string;
  purpose: string;
  bound_at: ISODateTime;
  dossier_id: string;
  dossier_version: number;
  dossier_status: string;
  document_id: string;
  document_type: string;
  classification: string;
  owner_type: string;
  document_version_id: string;
  /**
   * The dossier file name (owner, like HostBill: `<domain>-<document id>.<ext>`, e.g.
   * `tenfold-vn-14.pdf`; `-2`, `-3` for a second file of a type; a registrar-signed file keeps the
   * name of the file it replaced). Absent from older servers.
   */
  file_name?: string | null;
  version_number: number;
  is_current_version: boolean;
  mime_type: string;
  size_bytes: number;
  issued_at: string | null;
  expires_at: string | null;
  uploaded_at: ISODateTime;
  verification_status: string | null;
  /** Where the domain's copy was taken from. */
  copied_from?: import('../types').DocumentProvenance | null;
  /** The registrar's signature on this file (ADR-024 decision log 9–11). Absent from older servers. */
  registrar_signature?: DocumentRegistrarSignature | null;
}

/**
 * The registrar's (NĐK) signature on one file of a domain's dossier. The domain declaration is
 * signed automatically in every dossier (`automatic`); any other file is optional — staff send it
 * with "Gửi NĐK ký" (`requestRegistrarSignature`) when `can_request`.
 */
export interface DocumentRegistrarSignature {
  /** "NĐK đã ký": this file is a registrar-signed file. */
  signed: boolean;
  signed_at: ISODateTime | null;
  origin: 'AUTO' | 'MANUAL' | null;
  /** Registrar certificate name; null without `subject.read`. */
  signer: string | null;
  /** Replaced by its registrar-signed copy (kept as history, not sent). */
  replaced: boolean;
  /** A kind the registrar signs automatically (dossier.registrar_auto_sign_files). */
  automatic: boolean;
  automatic_kind: string | null;
  /** The open queue item for this file. */
  request: {
    item_id: string;
    origin: 'AUTO' | 'MANUAL';
    status: 'PENDING_CHECK' | 'REVIEW' | 'QUEUED' | 'IN_BATCH';
    queued_at: ISODateTime;
    requested_by: string | null;
    last_error: string | null;
  } | null;
  /** Whether "Gửi NĐK ký" is possible now (a PDF, not signed, not signed automatically, no open request). */
  can_request: boolean;
}

/** `POST /domains/{key}/documents/{document_version_id}/registrar-signature` (202). */
/** One check before the registrar signs (`signature_precheck`); `message` is Vietnamese. */
export interface SignaturePrecheckRow {
  code: string;
  status: 'PASS' | 'FAIL';
  message: string;
}

/**
 * "Ký SmartCA" answered: the checks ran in the request. `QUEUED` for the daily batch, or
 * `IN_BATCH` with the `batch` when sent with `now` ("Gửi ký ngay"). Refusals: 422
 * `PRECHECK_FAILED` (`details.reasons`, Vietnamese), 422 `INVALID`, 409 `SIGNING_OFF` /
 * `SMARTCA_NOT_CONFIGURED` / `SIGNING_NOT_READY` / `NOTHING_TO_SIGN`.
 */
export interface RegistrarSignatureRequested {
  item: { item_id: string; status: string };
  dossier_id: string;
  checks?: SignaturePrecheckRow[];
  batch?: { batch_id: string; status: string; item_count: number } | null;
  /** A declaration waiting for staff approval was accepted by this request ("NĐK duyệt khi ký"). */
  accepted_declaration?: boolean;
}

/** The dossier as VNNIC's HSTM holds it (`DomainVnnic.sync.hstm`). */
export interface DomainHstmDossier {
  /** HSTM documentId. */
  dossier_id: string | null;
  /** e.g. "Đăng ký mới". */
  document_type: string | null;
  /** HSTM returns no author: null. */
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'NOT_SENT' | string;
  reason: string | null;
  verification: import('../types').HstmVerification | null;
  files: {
    id: string;
    name: string;
    /** Bytes. */
    size: number | null;
    file_type: number;
    file_type_label: string | null;
    signed_status: number;
    signed_by_label: 'Ký bởi NĐK' | 'Ký bởi chủ thể' | null;
    created_at: string | null;
  }[];
}

export type EppObjectType = 'DOMAIN' | 'CONTACT' | 'HOST';

/** An EPP command on the domain or on a contact / host object it uses (no payloads). */
export interface DomainEppCommand {
  id: string;
  object_type: string;
  object_key: string | null;
  operation: string;
  state: RegistryCommandState;
  attempt: number;
  cltrid: string;
  svtrid: string | null;
  result_code: number | null;
  result_message: string | null;
  error_class: string | null;
  traffic_class: string;
  started_at: ISODateTime;
  completed_at: ISODateTime | null;
  latency_ms: number | null;
  correlation_id: string | null;
  workflow_id: string | null;
  workflow_type: string | null;
  registry_code: string;
  registry_name: string;
}

export interface DomainEppQuery {
  operation?: string;
  state?: string;
  object_type?: EppObjectType;
  /** Exact clTRID or svTRID. */
  trid?: string;
  limit?: number;
  /** Keyset cursor: `next_before` of the previous page. */
  before?: string;
}

/** `GET /domains/{key}/epp` */
export interface DomainEppPage {
  items: DomainEppCommand[];
  /** Every operation seen for the domain (for the filter). */
  operations: string[];
  next_before: string | null;
}

export interface DomainOrderItem {
  id: string;
  order_id: string;
  order_status: string;
  order_created_at: ISODateTime;
  order_total: DecimalString;
  order_currency: string;
  order_item_count: number;
  /** Domain of the order's first item: with `order_item_count`, the order's readable name (`orderLabel`). */
  order_first_domain: string | null;
  customer_id: string;
  customer_name: string;
  domain_name: string;
  operation: string;
  years: number;
  currency: string;
  amount: DecimalString;
  status: string;
  created_at: ISODateTime;
  workflow_id: string | null;
  workflow_type: string | null;
  hold_id: string | null;
  hold_status: string | null;
  invoice_id: string | null;
  invoice_series: string | null;
  invoice_number: number | null;
}

export interface DomainInvoiceLine {
  id: string;
  order_item_id: string | null;
  description: string;
  quantity: number;
  unit_price: DecimalString;
  amount: DecimalString;
  tax_code: string | null;
  tax_rate: DecimalString | null;
  tax_amount: DecimalString;
  is_credit: boolean;
}

export interface DomainInvoice {
  id: string;
  document_type: 'INVOICE' | 'CREDIT_NOTE';
  series: string;
  number: number;
  customer_id: string;
  customer_name: string;
  order_id: string | null;
  credits_invoice_id: string | null;
  /** `SERIES-NUMBER` of the invoice a credit note corrects. */
  credits_invoice_label: string | null;
  currency: string;
  subtotal: DecimalString;
  tax: DecimalString;
  total: DecimalString;
  issued_at: ISODateTime;
  /** Only the lines for this domain. */
  lines: DomainInvoiceLine[];
}

export interface DomainHold {
  id: string;
  amount: DecimalString;
  currency: string;
  purpose: string;
  status: string;
  order_item_id: string | null;
  capture_entry_id: string | null;
  expires_at: ISODateTime | null;
  created_at: ISODateTime;
  closed_at: ISODateTime | null;
}

export interface DomainRefund {
  id: string;
  order_item_id: string | null;
  amount: DecimalString;
  currency: string;
  reason: RecordedReason;
  status: string;
  journal_entry_id: string | null;
  created_at: ISODateTime;
}

export interface DomainPosting {
  id: string;
  account_id: string;
  /** Embeds owner IDs (e.g. `customer:<uuid>:wallet`); show `owner_name` and the account kind instead. */
  account_code: string;
  account_type: string;
  owner_type: string | null;
  owner_name: string | null;
  direction: 'DEBIT' | 'CREDIT';
  amount: DecimalString;
  currency: string;
}

export interface DomainJournalEntry {
  id: string;
  entry_type: string;
  /** Shown through <SystemReason> (a journal entry's description). */
  description: RecordedReason;
  resource_type: string | null;
  reverses_entry_id: string | null;
  posted_at: ISODateTime;
  postings: DomainPosting[];
}

/** `GET /domains/{key}/commerce` */
export interface DomainCommerce {
  billing_state: string;
  customer_id: string;
  customer_name: string;
  billing_currency: string;
  customer_expires_at: ISODateTime | null;
  registry_expires_at: ISODateTime | null;
  auto_renew: boolean;
  auto_renew_period: number;
  items: DomainOrderItem[];
  invoices: DomainInvoice[];
  holds: DomainHold[];
  refunds: DomainRefund[];
  journal_entries: DomainJournalEntry[];
}

export type TransferDirection = 'IN' | 'OUT';
export type TransferStatus = 'REQUESTED' | 'AUTHORIZED' | 'REGISTRY_PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'EXPIRED' | 'COMPLETED';
export type RegistrantChangeStatus = 'REQUESTED' | 'AUTHORIZED' | 'REGISTRY_PENDING' | 'COMPLETED' | 'REJECTED' | 'CANCELLED' | 'EXPIRED';

/** `GET /transfers` row. */
export interface TransferSummary {
  id: string;
  domain_id: string;
  ascii_name: string;
  unicode_name: string;
  direction: TransferDirection;
  status: TransferStatus;
  /** The gaining (OUT) or losing (IN) registrar. */
  other_registrar: string | null;
  /** Last trStatus seen at the registry. */
  registry_status: string | null;
  reject_reason: string | null;
  requested_at: ISODateTime;
  registry_pending_at: ISODateTime | null;
  auto_approve_at: ISODateTime | null;
  decided_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  customer_id: string;
  customer_name: string;
  registry_code: string;
  registry_name: string;
}

/** Actor fields: staff by display name, system components by name, the domain's own customer by its account name, others unnamed. */
export interface ActorRef {
  type: string | null;
  name: string | null;
}

export interface RelatedEvent {
  id: string;
  sequence: number;
  event_type: string;
  actor_type: string;
  actor_name: string | null;
  source: string;
  correlation_id: string | null;
  occurred_at: ISODateTime;
}

/**
 * Authorization evidence reduced to method, times and actor types (per side for registrant changes),
 * plus `actor_name` when the domain's own customer account confirmed.
 */
export type AuthorizationEvidence = Record<string, unknown>;

/** `GET /transfers/{id}` */
export interface TransferDetail extends TransferSummary {
  lifecycle_state: LifecycleState;
  transfer_state: string;
  registry_expires_at: ISODateTime | null;
  /** Whether an (encrypted) AuthInfo is on file. The value itself is never returned. */
  has_auth_info: boolean;
  requested_by_type: string;
  requested_by_name: string | null;
  decided_by_type: string | null;
  decided_by_name: string | null;
  order_item_id: string | null;
  order_id: string | null;
  order_item_status: string | null;
  order_item_amount: DecimalString | null;
  order_item_currency: string | null;
  order_item_years: number | null;
  /** The order's creation time, item count and first domain: its readable name (`orderLabel`). */
  order_created_at: ISODateTime | null;
  order_item_count: number | null;
  order_first_domain: string | null;
  workflow_id: string | null;
  workflow_type: string | null;
  workflow_state: string | null;
  workflow_step: string | null;
  registry_transaction_id: string | null;
  registry_operation: string | null;
  registry_transaction_state: RegistryCommandState | null;
  registry_cltrid: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  authorization_evidence: AuthorizationEvidence;
  events: RelatedEvent[];
}

export interface TransferQuery {
  status?: TransferStatus;
  direction?: TransferDirection;
  /** Prefix of the domain name. */
  domain?: string;
  limit?: number;
  before?: string;
}

/** `GET /registrant-changes` row. */
export interface RegistrantChangeSummary {
  id: string;
  domain_id: string;
  ascii_name: string;
  unicode_name: string;
  status: RegistrantChangeStatus;
  from_subject_id: string | null;
  from_subject_type: string | null;
  from_display_name: string | null;
  to_subject_id: string;
  to_subject_type: string;
  to_display_name: string | null;
  transfer_lock_opt_out: boolean;
  failure_reason: string | null;
  requested_at: ISODateTime;
  completed_at: ISODateTime | null;
  updated_at: ISODateTime;
  customer_id: string;
  customer_name: string;
}

/** `GET /registrant-changes/{id}` */
export interface RegistrantChangeDetail extends RegistrantChangeSummary {
  lifecycle_state: LifecycleState;
  from_version_number: number | null;
  from_country_code: string | null;
  to_version_number: number;
  to_country_code: string | null;
  to_verification_id: string | null;
  to_verification_method: string | null;
  to_verification_status: string | null;
  to_verified_at: ISODateTime | null;
  to_verification_expires_at: ISODateTime | null;
  new_dossier_id: string | null;
  new_dossier_version: number | null;
  new_dossier_status: string | null;
  requested_by_type: string;
  requested_by_name: string | null;
  workflow_id: string | null;
  workflow_type: string | null;
  workflow_state: string | null;
  workflow_step: string | null;
  registry_transaction_id: string | null;
  registry_operation: string | null;
  registry_transaction_state: RegistryCommandState | null;
  registry_cltrid: string | null;
  registry_code: string;
  registry_name: string;
  authorization_evidence: AuthorizationEvidence;
  events: RelatedEvent[];
}

export interface RegistrantChangeQuery {
  status?: RegistrantChangeStatus;
  domain?: string;
  limit?: number;
  before?: string;
}

export interface ExpiryNotice {
  id: string;
  notice_type: string;
  expiry_at: ISODateTime;
  policy_key: string;
  policy_version: string;
  created_at: ISODateTime;
}

export interface TransferLockRecord {
  id: string;
  lock_type: string;
  reason: RecordedReason;
  case_reference: string | null;
  applied_at: ISODateTime;
  applied_by_type: string;
  applied_by_name: string | null;
  auto_release_at: ISODateTime | null;
  released_at: ISODateTime | null;
  release_reason: RecordedReason | null;
}

/** `GET /domains/{key}/transfers` */
export interface DomainTransfers {
  transfer_state: string;
  auth_info_set_at: ISODateTime | null;
  transfers: (TransferSummary & { requested_by_type: string; requested_by_name: string | null; has_auth_info: boolean })[];
  registrant_changes: RegistrantChangeSummary[];
  expiry_notices: ExpiryNotice[];
  locks: TransferLockRecord[];
}

/** `GET /domains/{key}/audit` row: an audited action on the domain (no before / after images). */
export interface DomainAuditEntry {
  id: string;
  action: string;
  outcome: 'SUCCESS' | 'DENIED' | 'FAILED';
  reason: RecordedReason | null;
  actor_type: string;
  /** Staff display name or system component; null for customer actors. */
  actor_name: string | null;
  request_id: string | null;
  correlation_id: string | null;
  created_at: ISODateTime;
}

// ---------------------------------------------------------------------------
// Registry contact objects

/** `GET /contacts` row: one registry contact object (registry + handle). */
export interface ContactSummary {
  registry_code: string;
  registry_name: string;
  handle: string;
  /** Current roles. Empty when no domain uses the contact any more. */
  roles: ContactRole[];
  domain_count: number;
  total_domain_count: number;
  first_used_at: ISODateTime;
  last_assigned_at: ISODateTime;
  subject_id: string | null;
  subject_type: string | null;
  subject_version_number: number | null;
  subject_version_is_current: boolean | null;
  display_name: string | null;
  country_code: string | null;
}

export interface ContactQuery {
  /** Handle prefix or subject name. */
  q?: string;
  role?: ContactRole;
  registry?: string;
  limit?: number;
  /** `next_after` of the previous page. */
  after?: string;
}

export interface ContactList {
  items: ContactSummary[];
  next_after: string | null;
}

export interface ContactDomain {
  id: string;
  role: ContactRole;
  valid_from: ISODateTime;
  valid_until: ISODateTime | null;
  domain_id: string;
  ascii_name: string;
  unicode_name: string;
  lifecycle_state: LifecycleState;
  registry_expires_at: ISODateTime | null;
  customer_id: string;
  customer_name: string;
  subject_version_number: number | null;
}

export interface ContactCommand {
  id: string;
  operation: string;
  state: RegistryCommandState;
  attempt: number;
  cltrid: string;
  svtrid: string | null;
  result_code: number | null;
  result_message: string | null;
  started_at: ISODateTime;
  completed_at: ISODateTime | null;
  latency_ms: number | null;
  workflow_id: string | null;
  workflow_type: string | null;
  correlation_id: string | null;
}

/** `GET /contacts/{registry}/{handle}` */
export interface ContactDetail extends ContactSummary {
  subject: {
    subject_id: string;
    subject_type: string;
    status: string;
    version_id: string;
    version_number: number;
    is_current_version: boolean;
    display_name: string | null;
    country_code: string | null;
    created_at: ISODateTime;
    verification_status: string | null;
    managing_customer_id: string | null;
    managing_customer_name: string | null;
  } | null;
  domains: ContactDomain[];
  /** Null when the caller may not read registry transactions. */
  registry_transactions: ContactCommand[] | null;
  /** The data sent to the registry for this contact; null without `subject.read`. */
  details: ContactData | null;
}

/** A contact's data as the registry receives it (identity numbers only as masked hints). */
export interface ContactData {
  kind: string;
  name: string;
  organization: string | null;
  trade_name: string | null;
  gender: string | null;
  position: string | null;
  nationality: string | null;
  has_birth_date: boolean;
  email: string | null;
  phone: string | null;
  fax: string | null;
  address: { street: string[]; city: string; province: string | null; postal_code: string | null; country_code: string } | null;
  vn_address: { address: string; full_address: string; province_id: number; province_name: string; ward_id: number | null; ward_name: string | null } | null;
  identifiers: { type: string; country: string; hint: string | null }[];
}

/** `GET /domains/{key}/vnnic`: the domain's VNNIC dossier sync state and its latest attempt (no payloads). */
export interface DomainVnnic {
  sync: {
    dossier_id: string;
    dossier_version: number;
    operation: VnnicSyncOperation;
    status: VnnicSyncStatus;
    attempts: number;
    external_reference: string | null;
    last_error_code: VnnicSyncErrorCode | null;
    next_attempt_at: ISODateTime | null;
    status_changed_at: ISODateTime;
    last_transaction: {
      id: string;
      operation: string;
      status: VnnicTransactionStatus;
      attempt: number;
      external_reference: string | null;
      submitted_at: ISODateTime | null;
      completed_at: ISODateTime | null;
      error_code: VnnicSyncErrorCode | null;
      /** VNNIC's own message, as received. */
      error_message: string | null;
    } | null;
    /** The dossier as VNNIC's HSTM holds it (absent from older servers). */
    hstm?: DomainHstmDossier | null;
  } | null;
}

export interface PagedBefore<T> {
  items: T[];
  next_before: string | null;
}

// ---------------------------------------------------------------------------
// Staff actions on a domain (subsystems/registrar/facade/adapters/admin-api/src/admin/domain_actions.rs). Every body carries
// a reason, recorded in the audit log.

/** Registry outcome of a change applied now. */
export interface DomainChangeResult {
  status: 'APPLIED' | 'PENDING_CONFIRMATION';
  registry_transaction_id: string;
}

/** Grounds on which a registrar may refuse an outbound transfer (ICANN Transfer Policy, `policies/icann/transfer-policy.yaml`). */
export const TRANSFER_NACK_REASONS = [
  'EVIDENCE_OF_FRAUD',
  'UDRP_OR_URS_ACTION',
  'COURT_ORDER',
  'IDENTITY_DISPUTE',
  'PAYMENT_DISPUTE',
  'EXPRESS_REGISTRANT_OBJECTION',
  'WITHIN_POST_REGISTRATION_OR_TRANSFER_LOCK',
] as const;
export type TransferNackReason = (typeof TRANSFER_NACK_REASONS)[number];

/** Answer to an outbound transfer: applied at the registry, still unconfirmed, or (protected domain) waiting for staff confirmation. */
export type TransferResponseResult = DomainChangeResult | { status: 'AWAITING_CONFIRMATION'; pending_change_id: string };

/** A name server to set: a host name, or a name server under the domain itself with its glue addresses. */
export type NameserverInput = string | { host: string; addresses: string[] };

export interface DsInput {
  key_tag: number;
  algorithm: number;
  digest_type: number;
  digest: string;
  key_data?: { flags: number; protocol: number; algorithm: number; public_key: string } | null;
}

export type TransferCodeResult =
  | { domain_id: string; delivery: 'RETURNED'; auth_info: string }
  | { domain_id: string; delivery: 'SENT_TO_REGISTRANT'; recipient: string | null };

export interface RenewQuote {
  quote_id: string;
  years: number;
  subtotal: { amount: DecimalString; currency: string };
  tax: { amount: DecimalString; currency: string };
  total: { amount: DecimalString; currency: string };
  expires_at: ISODateTime;
  /** `POST /domains/{id}/renew-quote` only: why a renewal is refused now (null: it is not). */
  renewal_block?: RenewalBlock | null;
}

/**
 * Why a renewal is refused now (owner decisions 2026-10-06 / 2026-10-07): another renewal of the
 * domain has not finished, one finished less than 24 hours ago, or the domain carries
 * clientRenewProhibited. Staff may renew anyway (`overridable`) by confirming with a reason.
 */
export interface RenewalBlock {
  code: 'RENEWAL_IN_PROGRESS' | 'RENEWAL_RECENTLY_DONE';
  /** The refusal in Vietnamese, with its times. */
  message: string;
  running_since: ISODateTime | null;
  renewed_at: ISODateTime | null;
  renewable_at: ISODateTime | null;
  flagged: boolean;
  registry_expires_at: ISODateTime | null;
  customer_expires_at: ISODateTime | null;
  overridable: boolean;
}

/** How a registrant's consent was obtained (`EKYC`: only on changes recorded before 2026-10-05; Tino staff record the others). */
export type ConsentMethod = 'IN_PERSON' | 'SIGNED_DOCUMENT' | 'DIGITAL_SIGNATURE' | 'EMAIL_LINK' | 'PHONE_CALL' | 'EKYC';

/** `registry.registries.role`: who runs the RAA duties for domains held through a connection. */
export type ConnectionRole = 'REGISTRAR_OF_RECORD' | 'UPSTREAM_REGISTRAR';

/** The upstream registrar's (WebNIC) verification / suspension state of a domain. */
export interface UpstreamState {
  domain_id: string;
  verified: boolean | null;
  suspended: boolean | null;
  whois_privacy: boolean | null;
  checked_at: ISODateTime;
  changed_at: ISODateTime;
  last_error: string | null;
  resent_at: ISODateTime | null;
  resent_to_masked: string | null;
}

/** One role of `GET /domains/{id}/registry-contacts`: the registry's contact, read now, next to the platform's handle of that role. */
export interface RegistryContactRow {
  role: ContactRole;
  /** The handle the registry names for the role (null: none). */
  handle: string | null;
  /** The platform's current contact of the role. */
  our_handle: string | null;
  same_handle: boolean;
  read: 'FOUND' | 'NOT_FOUND' | 'UNSUPPORTED' | 'UNAVAILABLE' | 'NONE';
  /** UNAVAILABLE: the registry's outcome. */
  outcome?: string;
  contact?: {
    roid: string | null;
    statuses: string[];
    name: string | null;
    organization: string | null;
    street: string[];
    city: string | null;
    province: string | null;
    postal_code: string | null;
    country_code: string | null;
    /** The registry's own structured address (VNNIC). */
    full_address: string | null;
    email: string | null;
    voice: string | null;
    fax: string | null;
    position: string | null;
    sponsored_by_us: boolean | null;
    /** The identity number the registry holds, masked (an enterprise tax code is public). */
    identity: { individual: boolean; masked: string | null } | null;
  };
}

export interface DomainRegistryContacts {
  domain: string;
  sponsored_by_us: boolean;
  read_at: ISODateTime;
  contacts: RegistryContactRow[];
}

/** The registrant contact a registrant change to a subject sends (`GET`/`POST /domains/{id}/registrant-contact`). */
export interface RegistrantContact {
  subject_id: string;
  subject_version_id: string;
  /** The contact ID at the registry. */
  handle: string;
  /** Its recorded creation: null when never asked; SUCCESS when the registry holds it. */
  state: string | null;
  registry_transaction_id: string | null;
  created: boolean;
}

/** The notices staff send by hand ("Gửi email"). */
export type StaffEmailKey = 'contact.verify_email' | 'contact.data_reminder' | 'domain.expiry_reminder';

export interface DomainStaffEmail {
  template_key: StaffEmailKey;
  /** UPSTREAM_REGISTRAR: the upstream registrar (WebNIC) sends its own verification e-mail. */
  via: 'EMAIL' | 'UPSTREAM_REGISTRAR';
  unavailable: 'ICANN_ONLY' | 'ALREADY_VERIFIED' | 'NO_EMAIL' | 'NOT_LIVE' | null;
}

/** `POST /domains/{id}/emails/preview`: the e-mail as it would be sent now (nothing recorded). */
export interface DomainEmailPreview {
  /** The template that goes (an expired verification sends `contact.unverified_suspended`). */
  template_key: string;
  via: 'EMAIL' | 'UPSTREAM_REGISTRAR';
  recipient_email?: string;
  recipient_name?: string | null;
  locale?: 'vi' | 'en';
  subject?: string;
  html?: string;
  text?: string;
}

export interface DomainEmailSent {
  status: 'QUEUED' | 'SENT';
  via: 'EMAIL' | 'UPSTREAM_REGISTRAR';
  template_key?: string;
  notification_id?: string | null;
  recipient_masked: string | null;
}

export interface DomainActionsApi {
  /** `GET /domains/{id}/upstream`: the connection's role and (upstream only) the state it reports. */
  getDomainUpstream(domainId: string, opts?: CallOptions): Promise<{ role: ConnectionRole; state: UpstreamState | null }>;
  refreshDomainUpstream(domainId: string, body: { reason: string }, opts?: WriteOptions): Promise<UpstreamState>;
  /** "Gửi lại email xác minh" (WebNIC Resend Verification Email). */
  resendDomainVerification(domainId: string, body: { reason: string }, opts?: WriteOptions): Promise<{ status: 'SENT'; recipient_masked: string | null }>;
  setDomainNameservers(domainId: string, body: { nameservers: NameserverInput[]; reason: string }, opts?: WriteOptions): Promise<DomainChangeResult>;
  /** Cancels our own pending inbound transfer at the registry. */
  cancelTransfer(transferId: string, body: { reason: string }, opts?: WriteOptions): Promise<DomainChangeResult>;
  /** Approves or rejects a pending outbound transfer at the registry (a rejection names one of the transfer policy's grounds). */
  respondTransfer(transferId: string, body: { approve: boolean; nack_reason?: TransferNackReason; reason: string }, opts?: WriteOptions): Promise<TransferResponseResult>;
  setDomainDnssec(domainId: string, body: { ds: DsInput[]; reason: string }, opts?: WriteOptions): Promise<DomainChangeResult>;
  setDomainTransferLock(domainId: string, body: { locked: boolean; reason: string }, opts?: WriteOptions): Promise<DomainChangeResult>;
  /** "Khoá cập nhật" (`clientUpdateProhibited`) on or off (`PUT /domains/{id}/update-lock`). */
  setDomainUpdateLock(domainId: string, body: { locked: boolean; reason: string }, opts?: WriteOptions): Promise<DomainChangeResult>;
  /** "Khoá tên miền" (`clientHold`: no DNS) on or off (`PUT /domains/{id}/client-hold`). */
  setDomainClientHold(domainId: string, body: { locked: boolean; reason: string }, opts?: WriteOptions): Promise<DomainChangeResult>;
  /** "Liên hệ tại registry": domain:info → contact:info, read now (`domain.read` + `subject.read`). */
  getDomainRegistryContacts(domainId: string, opts?: CallOptions): Promise<DomainRegistryContacts>;
  /** The registrant contact a registrant change to `subjectId` sends, and whether it is created. */
  getRegistrantContact(domainId: string, subjectId: string, opts?: CallOptions): Promise<RegistrantContact>;
  /** "Tạo registrant mới": that contact created at the registry now (contact:create). */
  createRegistrantContact(domainId: string, body: { subject_id: string; reason: string }, opts?: WriteOptions): Promise<RegistrantContact>;
  /** "Gửi email": what may be sent by hand about the domain. */
  listDomainEmails(domainId: string, opts?: CallOptions): Promise<{ items: DomainStaffEmail[] }>;
  previewDomainEmail(domainId: string, body: { template_key: StaffEmailKey }, opts?: CallOptions): Promise<DomainEmailPreview>;
  sendDomainEmail(domainId: string, body: { template_key: StaffEmailKey; reason: string }, opts?: WriteOptions): Promise<DomainEmailSent>;
  issueDomainTransferCode(domainId: string, body: { reason: string }, opts?: WriteOptions): Promise<TransferCodeResult>;
  quoteDomainRenewal(domainId: string, body: { years: number }, opts?: CallOptions): Promise<RenewQuote>;
  /** `override_recent_renewal`: renew although the domain was renewed in the last 24 hours (the staff member confirmed it; the reason is required). */
  renewDomain(domainId: string, body: { quote_id: string; reason: string; override_recent_renewal?: boolean }, opts?: WriteOptions): Promise<{ order_id: string; workflow_id: string; years: number; status: 'PROCESSING' }>;
  /** The restore price on the owner's account (`POST /domains/{id}/restore-quote`). */
  quoteDomainRestore(domainId: string, opts?: CallOptions): Promise<RenewQuote>;
  /** Order + restore workflow (`POST /domains/{id}/restore`). */
  restoreDomain(domainId: string, body: { quote_id: string; reason: string }, opts?: WriteOptions): Promise<{ order_id: string; workflow_id: string; status: 'PROCESSING' }>;
  /** The transfer-in price (`POST /domain-transfers/quote`); with `subject_id`, the registrant is checked. */
  quoteTransferIn(body: { customer_id: string; domain: string; subject_id?: string | null; extra_fields?: Record<string, string> }, opts?: CallOptions): Promise<RenewQuote & { domain: string; unicode: string }>;
  /** Order + transfer-in workflow (`POST /domain-transfers`); the transfer code is never returned. */
  transferIn(
    body: { customer_id: string; quote_id: string; subject_id: string; auth_info: string; accepted_agreement_version: string; reason: string; extra_fields?: Record<string, string> },
    opts?: WriteOptions,
  ): Promise<{ order_id: string; domain_id: string; transfer_id: string; workflow_id: string; status: 'PROCESSING' }>;
  deleteDomain(domainId: string, body: { reason: string }, opts?: WriteOptions): Promise<DomainChangeResult>;
  startRegistrantChange(domainId: string, body: { to_subject_id: string; transfer_lock_opt_out?: boolean; reason: string }, opts?: WriteOptions): Promise<{ change_id?: string; workflow_id?: string; status: 'REQUESTED' | 'AWAITING_CONFIRMATION' | 'NAME_CHANGE_REQUEST'; pending_change_id?: string; name_change_id?: string; case?: string; registrant_handle?: string }>;
  confirmRegistrantChange(changeId: string, body: { side: 'CURRENT_REGISTRANT' | 'NEW_REGISTRANT'; method: ConsentMethod; reason: string }, opts?: WriteOptions): Promise<{ change_id: string; status: string }>;
  /** "Thử lại": a stopped registration runs again (with the subject's corrected data when there is). */
  retryRegistration(domainId: string, body: { reason: string }, opts?: WriteOptions): Promise<{ workflow_id: string; step: string; dossier_corrected: string | null }>;
  /** "Huỷ đăng ký": a stopped registration is cancelled and the held funds released. */
  cancelRegistration(domainId: string, body: { reason: string }, opts?: WriteOptions): Promise<{ workflow_id: string; status: 'CANCELLING' }>;
  /** "Kích hoạt lại đơn": a failed registration runs again on the same order and domain, the order item's amount held again from the customer's wallet. */
  reactivateRegistration(domainId: string, body: { reason: string }, opts?: WriteOptions): Promise<RegistrationReactivated>;
  /** The domain's admin / tech / billing contact takes a subject's current details (this domain only). */
  editDomainContact(domainId: string, role: ContactRole, body: { subject_id: string; reason: string }, opts?: WriteOptions): Promise<{ workflow_id: string; role: ContactRole; status: 'UPDATING' }>;
  listSubjectDomainContacts(subjectId: string, opts?: CallOptions): Promise<{ items: SubjectDomainContact[] }>;
  /** "Đồng bộ sang tên miền" for the chosen domains. */
  syncSubjectToDomains(subjectId: string, body: { domain_ids: string[]; reason: string }, opts?: WriteOptions): Promise<{ results: DomainSyncResult[] }>;
}

// ---------------------------------------------------------------------------
// Extra data a few TLDs require (config/tld-extra-fields.yaml, ADR-023;
// subsystems/identity/facade/adapters/admin-api/src/admin/extra_fields.rs)

/** Where a stored or offered value comes from. */
export type ExtraFieldSource = 'GIVEN' | 'EARLIER' | 'SUBJECT';

/** One field a TLD asks of the registrant, labels in the requested language. */
export interface ExtraFieldDef {
  key: string;
  label: string;
  help: string | null;
  type: 'text' | 'select' | 'date' | 'boolean' | 'country';
  required: 'always' | 'individuals' | 'organizations' | 'optional';
  applies_to: 'all' | 'individuals' | 'organizations';
  /** `select`: the accepted codes with their labels. */
  values: { value: string; label: string }[];
  min_length: number | null;
  max_length: number | null;
  pattern: string | null;
  /** Personal data: stored encrypted, only ever shown masked. */
  sensitive: boolean;
  /** The registrant field that fills the value when none is given. */
  default_from: string | null;
}

/** `GET /tlds/{zone}/extra-fields`: the fields of a registration form of the zone. */
export interface TldExtraFields {
  zone: string;
  /** "Thông tin bổ sung theo yêu cầu của đuôi .us". */
  heading: string;
  /** Empty for a zone that asks nothing. With `subject_id`: only the fields asked of that registrant. */
  fields: (ExtraFieldDef & {
    /** With `subject_id`: whether this registrant must give a value. */
    required_now?: boolean | null;
    /** With `subject_id`: what fills the field when left empty (a sensitive value only masked). */
    prefill?: { value: string | null; hint: string | null; source: ExtraFieldSource } | null;
  })[];
}

/** One value of a domain (`GET /domains/{key}/extra-fields`, `GET /domains/{key}` → `extra_fields`). */
export type DomainExtraFieldValue = ExtraFieldDef & {
  /** Null for a sensitive field (see `hint`) or when not set. */
  value: string | null;
  /** A select's code label. */
  value_label: string | null;
  /** A sensitive value, masked. */
  hint: string | null;
  is_set: boolean;
  source: ExtraFieldSource | null;
  set_by_type: string | null;
  set_at: ISODateTime | null;
};

export interface DomainExtraFields {
  zone: string | null;
  fields: DomainExtraFieldValue[];
}

export interface ExtraFieldsApi {
  /** The fields a registration / transfer form of `zone` asks for; with `subject_id`, what fills each one. */
  getTldExtraFields(zone: string, query: { lang?: string; subject_id?: string }, opts?: CallOptions): Promise<TldExtraFields>;
  getDomainExtraFields(key: string, query: { lang?: string }, opts?: CallOptions): Promise<DomainExtraFields>;
  /** Staff edit (`domain.write`, reason): `null` clears a value. Refused with REGISTRANT_INCOMPLETE (`extra.<key>`). */
  editDomainExtraFields(key: string, body: { values: Record<string, string | null>; reason: string; lang?: string }, opts?: WriteOptions): Promise<DomainExtraFields>;
}

export interface CoreApi extends DomainActionsApi, ExtraFieldsApi {
  getDomainContacts(key: string, opts?: CallOptions): Promise<DomainContacts>;
  getDomainRegistryState(key: string, opts?: CallOptions): Promise<DomainRegistryState>;
  /** "Đồng bộ với registry" (`registry.transaction.read`): one per domain at a time, 30 s apart. */
  syncDomainRegistry(key: string, opts?: WriteOptions): Promise<DomainRegistrySync>;
  getDomainDelegation(key: string, opts?: CallOptions): Promise<DomainDelegation>;
  listDomainDocuments(key: string, opts?: CallOptions): Promise<{ items: DomainDocument[] }>;
  /** "Gửi NĐK ký": one file of the domain's dossier joins the next registrar-signature batch. */
  requestRegistrarSignature(key: string, documentVersionId: string, body?: { now?: boolean }, opts?: WriteOptions): Promise<RegistrarSignatureRequested>;
  listDomainEpp(key: string, query: DomainEppQuery, opts?: CallOptions): Promise<DomainEppPage>;
  getDomainCommerce(key: string, opts?: CallOptions): Promise<DomainCommerce>;
  getDomainTransfers(key: string, opts?: CallOptions): Promise<DomainTransfers>;
  listDomainAudit(key: string, query: { limit?: number; before?: string }, opts?: CallOptions): Promise<PagedBefore<DomainAuditEntry>>;
  getDomainVnnic(key: string, opts?: CallOptions): Promise<DomainVnnic>;
  /**
   * One file of the dossier as VNNIC holds it (HSTM getFile) — `fileId` from `sync.hstm.files`;
   * before VNNIC assigned one, the document version id (the platform's own copy). Personal data:
   * `identity_document.view` and a reason (audited). `download`: saved as an attachment.
   */
  getVnnicFile(key: string, fileId: string, query: { reason: string; download?: boolean }, opts?: CallOptions): Promise<Blob>;
  /**
   * One file of the domain's dossier, the platform's own copy decrypted (`GET
   * /domains/{key}/documents/{version}/content`) — personal data: `identity_document.view` and a
   * reason (audited). `download`: saved as an attachment under its dossier file name.
   */
  getDomainDocumentContent(key: string, documentVersionId: string, query: { reason: string; download?: boolean }, opts?: CallOptions): Promise<Blob>;
  listContacts(query: ContactQuery, opts?: CallOptions): Promise<ContactList>;
  getContact(registry: string, handle: string, opts?: CallOptions): Promise<ContactDetail>;
  listTransfers(query: TransferQuery, opts?: CallOptions): Promise<PagedBefore<TransferSummary>>;
  getTransfer(id: string, opts?: CallOptions): Promise<TransferDetail>;
  listRegistrantChanges(query: RegistrantChangeQuery, opts?: CallOptions): Promise<PagedBefore<RegistrantChangeSummary>>;
  getRegistrantChange(id: string, opts?: CallOptions): Promise<RegistrantChangeDetail>;
}
